import { useTranslation } from 'react-i18next';
import { ProgressBar, Button, SyntaxIcon } from '@expressable/syntax-ds';
import { CaretLeft } from '@phosphor-icons/react';
import {
  ScreenerLandingPage,
  ScreenerEnterDob,
  ScreenerLoadingScreener,
  ScreenerNoScreenerFound,
  ScreenerRunningScreenerQuestions,
  ScreenerFinished,
} from 'domains/signup/screener/Steps';
import { ScreenerEnterTarget } from 'domains/signup/sharedSteps/EnterTarget';
import { ScreenerContext } from 'domains/signup/screener/context/screenerContext';
import { DocumentTitle } from 'components/DocumentTitle';
import { useScreenerProgress } from './useScreenerProgress';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffectOnce } from 'react-use';
import { identifyScreenerLDUser } from 'domains/signupDirect/misc/launchDarklyTracking';

const BaseScreenerWrapper: React.FC = () => {
  const hasBack = ScreenerContext.useSelector(state => state.can({ type: 'back' }));
  const { send } = ScreenerContext.useActorRef();
  const { t } = useTranslation();
  const currentQuestionIndex = ScreenerContext.useSelector(({ context }) => context.currentQuestionIndex ?? 0);
  const progress = useScreenerProgress(currentQuestionIndex);
  const { signupScreenerProgressbar } = useFlags();

  const isLandingPage = ScreenerContext.useSelector(state => state.matches('landingPage'));
  const screenerPageMaxWidth = isLandingPage ? 'max-w-[1000px]' : 'max-w-[600px]';
  const LDClient = useLDClient();
  useEffectOnce(() => {
    if (LDClient) {
      identifyScreenerLDUser(LDClient, {});
    }
  });

  return (
    <div className="flex flex-col min-h-full items-center px-24 md:px-0">
      <DocumentTitle>{t('screener.pageTitle')}</DocumentTitle>
      <div className={`${screenerPageMaxWidth} w-full min-h-full self-center py-64 sm-d:py-40`}>
        {signupScreenerProgressbar && !isLandingPage && (
          <ProgressBar className="w-full h-8 mb-64 sm-d:mb-40 text-purple-300" progress={progress} />
        )}
        {hasBack && (
          <Button variant="secondary" size="md" className="mb-24 font-semibold" onClick={() => send({ type: 'back' })}>
            <SyntaxIcon icon={CaretLeft} />
            {t('screener.backButton')}
          </Button>
        )}
        <ScreenerLandingPage />
        <ScreenerEnterTarget />
        <ScreenerEnterDob />
        <ScreenerLoadingScreener />
        <ScreenerNoScreenerFound />
        <ScreenerRunningScreenerQuestions />
        <ScreenerFinished />
      </div>
    </div>
  );
};

export default BaseScreenerWrapper;
